import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './Auth.css';

const Register: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [company, setCompany] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password.length < 6) {
            alert('Password must be at least 6 characters.');
            return;
        }
        if (!firstName || !lastName || !phone) {
            alert('Please fill in all required fields.');
            return;
        }
        if (!/^\d{10}$/.test(phone)) {
            alert('Phone number must be exactly 10 digits.');
            return;
        }
        if (password !== confirmPassword) {
            alert('Passwords do not match!');
            return;
        }
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            const now = Timestamp.now();
            // 写入用户信息
            await setDoc(doc(db, "users", user.uid), {
                email,
                firstName,
                lastName,
                address,
                phone,
                company,
                registerTime: now,
                modifyTime: now,
                lastLoginTime: now,
                grade: 0,
                consumption: 0,
                remark: "",
                tax: true,
                status: true,
                emailVerified: false // 新增：邮箱验证状态
            });
            // 新增：初始化空购物车
            await setDoc(doc(db, "carts", user.uid), {
                items: []
            });
            await sendEmailVerification(user);
            await auth.signOut();
            alert('Registration successful! Please check your email to verify your account & sign in after verification.');
            navigate('/login'); // 跳转到登录页
        } catch (error: any) {
            // 处理各种错误情况，提供友好的错误提示
            if (error.code === 'auth/email-already-in-use') {
                alert('This email is already registered. Please sign in instead, or use a different email address.');
                navigate('/login');
            } else if (error.code === 'auth/invalid-email') {
                alert('Invalid email address. Please check and try again.');
            } else if (error.code === 'auth/weak-password') {
                alert('Password is too weak. Please use a stronger password.');
            } else if (error.code === 'auth/network-request-failed') {
                alert('Network error. Please check your internet connection and try again.');
            } else {
                alert(error.message || 'Registration failed. Please try again.');
            }
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-box">
                <h2>Create Account</h2>
                <form onSubmit={handleRegister}>
                    <input
                        type="email"
                        placeholder="Email*"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                    />
                    <div className="password-field">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password*"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            minLength={6}
                        />
                        <button
                            type="button"
                            className="show-btn"
                            onClick={() => setShowPassword(v => !v)}
                            tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    {password && password.length < 6 && (
                        <div style={{ color: 'red', fontSize: '0.95em', marginBottom: 8 }}>
                            Password must be at least 6 characters.
                        </div>
                    )}
                    <div className="password-field">
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm Password*"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            className="show-btn"
                            onClick={() => setShowConfirmPassword(v => !v)}
                            tabIndex={-1}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                            {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <input
                        type="text"
                        placeholder="First Name*"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Last Name*"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Address"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        autoComplete="street-address"
                    />
                    <input
                        type="tel"
                        placeholder="Phone*"
                        value={phone}
                        onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} // 只允许数字
                        required
                        inputMode="tel"
                        pattern="\d{10}"
                        maxLength={10}
                    />
                    <input
                        type="text"
                        placeholder="Company (optional)"
                        value={company}
                        onChange={e => setCompany(e.target.value)}
                    />

                    <button type="submit">Register</button>
                </form>
                <p>
                    Already have an account? <Link to="/login">Sign in</Link>
                </p>
            </div>
        </div>
    );
};

export default Register;